import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { Mail, Lock, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const LoginModal = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { setTokenAndUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => { setEmail(''); setPassword(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast({ title: 'Faltan datos', description: 'Ingresa tu correo y contraseña' });
      return;
    }
    setLoading(true);
    try {
      const r = await api.post('/auth/login', { email: email.trim().toLowerCase(), password });
      setTokenAndUser(r.data.token, r.data.user);
      toast({ title: '¡Bienvenido!', description: r.data.user.name || r.data.user.email });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Correo o contraseña incorrectos' });
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const goRecover = () => { onOpenChange(false); reset(); navigate('/recuperar-contrasena'); };
  const goRegister = () => { onOpenChange(false); reset(); navigate('/registro'); };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md rounded-2xl bg-neutral-950 border-white/10 text-white" data-testid="login-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl text-white">Ingresar</DialogTitle>
          <DialogDescription className="text-white/60">
            Usa tu correo y contraseña, o continúa con Google.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Correo electrónico"
              type="email"
              autoComplete="email"
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
              data-testid="login-modal-email"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              type="password"
              autoComplete="current-password"
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
              data-testid="login-modal-password"
            />
          </div>

          <div className="flex justify-end -mt-1">
            <button type="button" onClick={goRecover} className="text-xs text-white/60 hover:text-orange-400" data-testid="login-modal-forgot">
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-black font-semibold" data-testid="login-modal-submit">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ingresando...</> : 'Ingresar'}
          </Button>

          <div className="flex items-center gap-3 my-2">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-white/40">o</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <Button type="button" onClick={googleLogin} variant="outline" className="w-full bg-white text-black hover:bg-white/90 border-white" data-testid="login-modal-google">
            <svg viewBox="0 0 48 48" className="w-4 h-4 mr-2"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 0-24c3 0 5.9 1.1 8 3l5.7-5.7A20 20 0 1 0 24 44a20 20 0 0 0 19.6-16 20 20 0 0 0 0-7.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.9 1.1 8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"/><path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.4-5.1l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5A20 20 0 0 0 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.6l6.2 5.2C41.2 34.7 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continuar con Google
          </Button>

          <p className="text-[11px] text-white/40 text-center">Al continuar aceptas nuestros Términos y Aviso de Privacidad. Debes ser mayor de edad (18+).</p>

          <div className="text-center pt-1">
            <button
              type="button"
              onClick={goRegister}
              className="text-sm text-white/70 hover:text-orange-400"
              data-testid="login-modal-register"
            >
              ¿No tienes cuenta? <span className="font-bold text-orange-400 underline">Crear cuenta</span>
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
